import { BrokenImage } from '@mui/icons-material'
import { Skeleton, Stack, SxProps, Theme, Typography, styled } from '@mui/material'
import { FC, ImgHTMLAttributes, ReactEventHandler, ReactNode, useEffect, useState } from 'react'

export interface ImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'loading' | 'onError' | 'onLoad' | 'onLoadStart'
  > {
  fallback?: ReactNode
  skeleton?: boolean
  sx?: SxProps<Theme>
  onLoadEnd?: () => void
  onLoadError?: ReactEventHandler<HTMLImageElement>
  onLoadStart?: () => void
}

const Image: FC<ImageProps> = ({
  fallback,
  height,
  skeleton = true,
  sx,
  width,
  onLoadEnd,
  onLoadError,
  onLoadStart,
  ...props
}) => {
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(!!props.src)

  const actualHeight = height ?? width
  const actualWidth = width ?? height

  useEffect(() => {
    props.src && onLoadStart && onLoadStart()
  }, [onLoadStart, props.src])

  const _onLoadEnd = () => {
    setLoading(false)
    onLoadEnd && onLoadEnd()
  }

  const _onError = (error) => {
    setError(true)
    setLoading(false)
    onLoadError && onLoadError(error)
  }

  if ((!loading && !props.src) || (error && !fallback)) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          bgcolor: 'grey.300',
          borderRadius: 1,
          height: actualHeight,
          position: 'relative',
          width: actualWidth,
        }}
      >
        <BrokenImage fontSize="large" sx={{ color: 'grey.500' }} />
        {props.alt && (
          <Typography
            sx={{
              position: 'absolute',
              left: (theme) => theme.spacing(2),
              right: (theme) => theme.spacing(2),
              bottom: (theme) => theme.spacing(1),
              lineHeight: 1.4,
              overflowWrap: 'break-word',
              whiteSpace: 'break-spaces',
            }}
            variant="caption"
          >
            {props.alt}
          </Typography>
        )}
      </Stack>
    )
  }

  return (
    <>
      {loading && skeleton && (
        <Skeleton
          height={actualHeight}
          width={actualWidth}
          sx={{ ...sx, transform: 'scaleY(1)' }}
          variant="rectangular"
        />
      )}
      {props.src && !error ? (
        <StyledImage
          {...props}
          style={{ height, width }}
          sx={{ ...sx, ...(loading && { position: 'absolute', visibility: 'hidden' }) }}
          onLoad={_onLoadEnd}
          onError={_onError}
        />
      ) : (
        !loading && (fallback ?? props.alt)
      )}
    </>
  )
}

const StyledImage = styled('img')`
  border-radius: ${({ theme }) => theme.spacing(1)};
`

export default Image
